import { makeAutoObservable } from "mobx";
import { ListEntityStore, Sorting, GridFilter } from "../../common/store/types";
import { getConditionsFromFilters } from "../../common/services/filterService";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { RootStore } from "../../../store";
import { Country } from "../../../api/graphql";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { countryService } from "../services/countryService";
import { InitializeParams } from "../../../store/types";
import { runInAction } from "mobx";
import {
  CountryGridDataStore,
  CountryListProfile,
} from "./CountryGridDataStore";
import { countryDisplayId } from "./CountryCommonStore";
import {
  DeletionBlockers,
  deletionBlockersFromQuery,
  deletionBlockersText,
  deletionMessages,
} from "../../../common/deletion/deletionBlockersText";

import { filterValues2Expression } from "../../common/services/filterService";
import _ from "lodash";
import { isGenerator } from "../../../common/utils";
import {
  applyClientSearch,
  applyClientSort,
  applyClientPagination,
  applyPrismaWhereFilters,
} from "../../common/services/clientSideFilterService";

export class CountryListStore implements ListEntityStore {
  rootStore: RootStore;
  status: StoreStatus = "uninitialized";
  service = countryService;
  countryList: Country[] = [];
  reloadedCountry: Country | null = null;
  webPageId: string = "";
  storeId?: string;
  pageSize = 10;
  currentPage = 1;
  lastLoadedPage = 0;
  cursorKey: string | null = null;
  lastSortedColumn: string | null = null;
  searchValue: string = "";
  lastFilteredValue: string = "";
  hasMorePages = false;
  errorDialogOpened: boolean = false;
  confirmDeleteDialogOpened: boolean = false;
  confirmDeleteId: string = "";
  confirmDeleteDisplayId: string = "";
  deleteCheck:
    | "idle"
    | "checking"
    | "free"
    | "blocked"
    | "failed"
    | "deleting" = "idle";
  deleteBlockers: DeletionBlockers | null = null;
  deleteToken: number = 0;
  deleteStuck: boolean = false;

  errorDescription: string = "Error";
  errorTitle: string = "";
  originalError: any;
  selectedRows: (string | number)[] = [];
  fixedFilter: any;
  whereFilters: any = undefined;
  grid: CountryGridDataStore;
  loadingError = "";
  callbackParams: CallbackDataParams | null = null;
  usingCache: boolean = false;
  cachedTotal: number = 0;

  debounceLoadFirstPage!: ReturnType<typeof _.debounce>;
  selectedRowId: string | null = null;

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.storeId = storeId;
    this.grid = new CountryGridDataStore(rootStore);

    this.debounceLoadFirstPage = _.debounce(
      async () => await this.loadFirstPage(true),
      500
    );

    makeAutoObservable(this);
  }

  deleteRow(id: string) {
    // Mientras se borra otra fila, nada cambia: el resultado es de esa fila.
    if (this.deleteCheck === "deleting") return;
    // Cada apertura lleva su token (lo lee checkDeletion al empezar).
    this.deleteToken++;
    const data = this.countryList.filter((x) => x.id === id);
    this.confirmDeleteId = id;
    this.selectedRowId = id;
    this.confirmDeleteDisplayId =
      (data[0] && countryDisplayId(data[0] as any, this.rootStore.langStore)) ||
      id;
    // Se abre verificando: el Ok espera la respuesta de deletionBlockers.
    this.deleteCheck = "checking";
    this.deleteBlockers = null;
    this.confirmDeleteDialogOpened = true;
    return this.checkDeletion(id);
  }

  *newCountrySavedWithId(id: string) {
    if (id) {
      yield this.fetch(() => this.reloadCountry(-1, id));
      this.selectedRowId = id;
    }
  }

  *updateCountrySavedWithId(id: string) {
    if (id) {
      const index = this.findIndexById(id);
      yield this.fetch(() =>
        this.reloadCountry(index !== null ? index : -1, id)
      );
      this.selectedRowId = id;
      this.rootStore.showSnackbar(
        this.rootStore.langStore.itemUpdatedMayHaveMoved,
        "info"
      );
    }
  }

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      const id = params.returnData.resultId;

      if (
        params.callerData?.fieldName === "create" ||
        params.callerData?.fieldName === "duplicate"
      ) {
        yield this.newCountrySavedWithId(id);
      } else if (params.callerData?.fieldName === "update") {
        yield this.updateCountrySavedWithId(id);
      }
    }
  }

  closeErrorDialog(): void {
    this.errorDialogOpened = false;
  }

  isActionEnabled(action: "update" | "retrieve" | "delete" | "create") {
    return this.rootStore.authStore.hasPermissions(`country_${action}`);
  }

  closeConfirmDeleteDialog(): void {
    // Mientras se borra no se cierra: el resultado del borrado se aplica a este diálogo.
    // Si el borrado no responde en 30 s (deleteStuck) se puede cerrar: el token cambia y
    // lo que llegue tarde ya no abre nada.
    if (this.deleteCheck === "deleting" && !this.deleteStuck) return;
    // Cada apertura lleva su token: lo que llegue de una apertura anterior ya no vale.
    this.deleteToken++;
    this.deleteStuck = false;
    this.confirmDeleteId = "";
    this.confirmDeleteDisplayId = "";
    this.deleteCheck = "idle";
    this.deleteBlockers = null;
    this.confirmDeleteDialogOpened = false;
  }

  get confirmDeleteBlockedText(): { title: string; lines: string[] } | null {
    if (this.deleteCheck !== "blocked" || !this.deleteBlockers) return null;
    // Una fila sin nombre propio muestra su id como nombre: el título nunca lleva el id.
    const display =
      this.confirmDeleteDisplayId === this.confirmDeleteId
        ? ""
        : this.confirmDeleteDisplayId;
    return deletionBlockersText(
      this.deleteBlockers,
      this.rootStore.langStore,
      display
    );
  }

  get confirmDeleteTitle(): string {
    const blocked = this.confirmDeleteBlockedText;
    if (blocked) return blocked.title;
    return this.rootStore.langStore.confirmDeleteDialogTitle(
      this.rootStore.langStore.CountrySingular,
      this.confirmDeleteDisplayId
    );
  }

  get confirmDeleteDescription(): string {
    if (this.deleteCheck === "checking")
      return deletionMessages(this.rootStore.langStore).checking;
    if (this.deleteCheck === "deleting")
      return deletionMessages(this.rootStore.langStore).deleting;
    const blocked = this.confirmDeleteBlockedText;
    return blocked
      ? blocked.lines.join("\n")
      : this.rootStore.langStore.confirmDeleteDialogDescription;
  }

  *checkDeletion(id: string): any {
    // Se llama al abrir el diálogo, antes de cualquier espera: el token es el de esta apertura.
    const token = this.deleteToken;
    // La respuesta (o el fallo) vale solo para la apertura que la pidió, y solo mientras espera.
    const isCurrent = () =>
      this.deleteToken === token &&
      this.confirmDeleteId === id &&
      this.confirmDeleteDialogOpened &&
      this.deleteCheck === "checking";
    // Si el server no responde en 10 s se toma por fallida: pregunta como siempre y el Ok se habilita.
    const timer = setTimeout(() => {
      runInAction(() => {
        if (isCurrent()) this.deleteCheck = "failed";
      });
    }, 10000);
    try {
      const api = yield* getApi(
        this.rootStore.authStore,
        this.rootStore.activityStore
      );
      const result = yield api.deletionBlockers({ entity: "Country", id });
      if (!isCurrent()) return;
      const blockers = deletionBlockersFromQuery(result.deletionBlockers);
      this.deleteBlockers = blockers.blocked ? blockers : null;
      this.deleteCheck = blockers.blocked ? "blocked" : "free";
    } catch {
      // La consulta falló (red, permisos, otro error): se pregunta como
      // siempre y el server vuelve a verificar al borrar. Su error no se muestra.
      if (isCurrent()) {
        this.deleteCheck = "failed";
      }
    } finally {
      clearTimeout(timer);
    }
  }

  *confirmOkDelete(): any {
    // Solo se confirma con el diálogo abierto y una fila: no mientras consulta, ni si
    // está en uso (el diálogo solo ofrece Cerrar), ni si ya se está borrando (doble click).
    if (!this.confirmDeleteDialogOpened || !this.confirmDeleteId) return;
    if (this.deleteCheck !== "free" && this.deleteCheck !== "failed") return;
    const id = this.confirmDeleteId;
    const token = this.deleteToken;
    this.deleteCheck = "deleting";
    this.deleteStuck = false;
    // A los 30 s sin respuesta se habilita Cancelar: cerrar cambia el token y el
    // resultado tardío no abre ningún diálogo.
    const timer = setTimeout(() => {
      runInAction(() => {
        if (this.deleteToken === token && this.deleteCheck === "deleting")
          this.deleteStuck = true;
      });
    }, 30000);
    try {
      const api = yield* getApi(
        this.rootStore.authStore,
        this.rootStore.activityStore
      );
      const result = yield api.countryDelete({ id });
      if (result.countryDelete.id !== id) {
        throw Error(`${id} can't deleted`);
      }
    } catch (error) {
      clearTimeout(timer);
      // Cerró mientras tardaba: el diálogo ya no es de este borrado, no se muestra nada.
      if (this.deleteToken !== token) return;
      // Rechazado (en uso desde la consulta, un validador del proyecto,
      // permisos, red): se cierra la confirmación y el diálogo de error dice por qué.
      this.deleteCheck = "idle";
      this.closeConfirmDeleteDialog();
      this.showApiError(error);
      return;
    }
    clearTimeout(timer);
    // Si cerró mientras tardaba, solo se recarga la lista, en silencio.
    if (this.deleteToken === token) {
      this.deleteCheck = "idle";
      this.closeConfirmDeleteDialog();
    }
    try {
      yield this.fetch(() => this.loadFirstPage(true));
    } catch {
      // fetch ya mostró el error de la recarga.
    }
  }

  *initialize(params?: InitializeParams) {
    if (this.status === "uninitialized") {
      this.setStatus("processing");
      try {
        if (params?.uiState) {
          if (params.uiState.currentPage !== undefined) {
            this.currentPage = params.uiState.currentPage;
          }
          if (params.uiState.searchValue !== undefined) {
            this.searchValue = params.uiState.searchValue;
          }
          if (params.uiState.whereFilters !== undefined) {
            this.whereFilters = params.uiState.whereFilters;
          }
          if (params.uiState.orderBy !== undefined) {
            this.grid.setSorting(params.uiState.orderBy);
          }
        } else {
          const profile: CountryListProfile =
            this.rootStore.authStore.getProfile(this.grid.ProfileKey);
          if (profile) {
            try {
              this.grid.setGridState(profile);
            } catch (error) {
              console.error(
                "[DEBUG-EntityListStore] Failed to restore grid state:",
                error
              );
            }
          }
        }
        yield this.fetch(() => this.loadFirstPage(true));
        this.setStatus("ok");
      } catch (error) {
        this.setStatus("error");
        this.showApiError(error);
      }
    }
  }

  findIndexById(id: string) {
    for (let i = 0; i < this.countryList.length; i++) {
      if (this.countryList[i].id === id) {
        return i;
      }
    }
    return null;
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get breadcrumbLabel(): string {
    return this.rootStore.langStore.CountryPlural;
  }

  clickOnRow(row: any) {
    if (row.id !== this.selectedRowId) {
      this.setSelectedRow(row.id);
    }
  }

  viewRow(id: string, event?: React.MouseEvent, _source?: string) {
    this.openWithIntent(id, "view", event);
  }

  viewRowInRoute(id: string) {
    this.rootStore.router.goTo(
      "/country/view/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  editRow(id: string, event?: React.MouseEvent, _source?: string) {
    this.openWithIntent(id, "update", event);
  }

  handleRowClick(row: any, event?: React.MouseEvent) {
    const intent = this.isActionEnabled("update") ? "update" : "view";
    this.openWithIntent(row.id, intent, event);
  }

  // Resolves overlay (drawer vs route) from the user's toggle preference,
  // with Cmd/Ctrl inverting on desktop; on mobile, only the toggle matters.
  private openWithIntent(
    id: string,
    mode: "view" | "update",
    event?: React.MouseEvent
  ) {
    if (this.shouldOpenInDrawer(event)) {
      this.rootStore.openDrawerView("country", id, mode);
    } else {
      this.rootStore.router.goTo(
        "/country/" + mode + "/" + id,
        undefined,
        undefined,
        undefined,
        this.breadcrumbLabel
      );
    }
  }

  private shouldOpenInDrawer(event?: React.MouseEvent): boolean {
    if (this.rootStore.uiStore.isMobile) return this.grid.viewMode === "drawer";
    const isDrawerDefault = this.grid.viewMode === "drawer";
    const inverted = !!(event?.metaKey || event?.ctrlKey);
    return inverted ? !isDrawerDefault : isDrawerDefault;
  }

  duplicateRow(id: string, _event?: React.MouseEvent, _source?: string) {
    this.rootStore.router.goTo(
      "/country/create/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  createNewCountry(_event?: React.MouseEvent) {
    this.rootStore.router.goTo(
      "/country/create",
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  *onSearch(value: string) {
    this.searchValue = value;
    yield this.debounceLoadFirstPage();
  }

  setWhereFilters(filters: GridFilter[]) {
    const timeZone = this.rootStore.langStore.timeZone;
    const subFilters = this.grid.subFilters;
    this.whereFilters = getConditionsFromFilters(
      filters,
      this.grid.columnTypes,
      (
        fieldName: string,
        columnType: string,
        operator: string,
        value: string
      ) => {
        return filterValues2Expression(
          fieldName,
          columnType,
          operator,
          value,
          timeZone,
          subFilters
        );
      }
    );
  }

  *setFilters(filters: GridFilter[]) {
    this.grid.setSelectedFilters(filters);
    yield this.debounceLoadFirstPage();
  }

  async getFilteredList(value: string) {
    await this.onSearch(value);
    return this.countryList.map((x) => x);
  }

  get processingDescription() {
    return this.rootStore.langStore.processingDescription;
  }

  get totalRows() {
    return this.usingCache ? this.cachedTotal : this.countryList.length;
  }

  get hasNextPage() {
    return this.currentPage < this.lastLoadedPage || this.hasMorePages;
  }

  get countryCurrentPage() {
    // Para entidades cacheables, la lista ya está paginada por loadFromCache
    return this.usingCache
      ? this.countryList
      : this.countryList.slice(
          (this.currentPage - 1) * this.pageSize,
          this.currentPage * this.pageSize
        );
  }

  onRowSelectionChange(rowsSelection: (string | number)[]) {
    this.selectedRows = rowsSelection;
  }

  *onSortingChange(sorting: Sorting[]) {
    this.grid.setSorting(sorting);
    yield this.loadFirstPage(true);
  }

  clearList() {
    this.currentPage = 1;
    this.countryList = [];
    this.cursorKey = null;
    this.lastLoadedPage = 0;
  }

  *loadFirstPage(clear: boolean): any {
    yield this.fetch(() => this.loadPage(clear));
  }

  *loadPage(clear: boolean = false): any {
    if (
      !clear &&
      this.lastLoadedPage >= this.currentPage &&
      this.pageSize < this.countryList.length
    ) {
      this.status = "ok";
      return;
    }

    // Use cache for this cacheable entity
    const cacheStore = this.rootStore.referenceCacheStore;
    yield cacheStore.ensureLoaded("Country");
    this.loadFromCache(clear);
    return;
  }

  loadFromCache(clear: boolean = false): void {
    const cacheStore = this.rootStore.referenceCacheStore;
    const allData = cacheStore.getRawData("Country") as Country[];

    // Apply search filter
    let filteredData = this.searchValue.trim()
      ? applyClientSearch(
          allData,
          this.searchValue.trim(),
          this.grid.searchFields
        )
      : allData;

    // Apply Prisma WHERE filters (from MRT column filters)
    if (this.whereFilters && this.whereFilters.length > 0) {
      filteredData = applyPrismaWhereFilters(filteredData, this.whereFilters);
    }

    // Apply sorting
    const sorting = this.grid.sorting.map((s) => ({
      columnName: s.columnName,
      direction: s.direction as "asc" | "desc",
    }));
    const sortedData = applyClientSort(filteredData, sorting);

    // Apply pagination
    const { items, hasMore, total } = applyClientPagination(
      sortedData,
      this.currentPage,
      this.pageSize
    );

    runInAction(() => {
      if (clear) {
        this.clearList();
      }
      this.usingCache = true;
      this.cachedTotal = total;
      this.lastSortedColumn = this.grid.sorting[0]?.columnName || null;
      this.lastLoadedPage = this.currentPage;
      this.hasMorePages = hasMore;
      this.countryList = items as Country[];
      this.setStatus("ok");
    });
  }

  setSelectedRow(id: string | null) {
    this.selectedRowId = id;
  }

  *saveProfile(): any {
    yield this.rootStore.authStore.saveProfile(
      this.grid.ProfileKey,
      this.grid.gridState
    );
  }

  *onPageSizeChange(pageSize: number) {
    this.pageSize = pageSize;
    yield this.loadFirstPage(true);
  }

  *onPageChange(currentPage: number) {
    this.currentPage = currentPage;
    yield this.fetch(() => this.loadPage());
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.rootStore.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    // Siempre los dos: un error sin título o sin texto no muestra los del error anterior.
    this.errorTitle = title || this.rootStore.langStore.errorTitle;
    this.errorDescription = description || this.rootStore.langStore.serverError;
    this.errorDialogOpened = true;
  }

  // El título y el texto de un error para el diálogo. Un error que getApiError
  // no reconoce (code vacío) trae el texto crudo de la respuesta —el JSON de
  // GraphQL, la consulta enviada—: al diálogo va el texto genérico.
  errorDialogText(error: unknown): { title: string; message: string } {
    const langStore = this.rootStore.langStore;
    const apiError =
      error instanceof Error ? getApiError(error, langStore) : null;
    return apiError && apiError.code
      ? { title: apiError.title, message: apiError.message }
      : { title: langStore.errorTitle, message: langStore.serverError };
  }

  showApiError(error: unknown) {
    const { title, message } = this.errorDialogText(error);
    this.showErrorDialog(error, title, message);
  }

  *reloadCountry(index: number, id: string): any {
    const data = yield this.getOneCountry(id);

    if (!data || !data.countryOne) {
      console.error(`Failed to reload Country with id ${id}`, data);
      return;
    }

    if (
      index >= 0 &&
      this.countryList[index] &&
      this.countryList[index].id === id
    ) {
      this.countryList[index] = data.countryOne as Country;
      this.reloadedCountry = this.countryList[index];
    } else {
      this.countryList.unshift(data.countryOne as Country);
      this.reloadedCountry = this.countryList[0];
    }
  }

  *getOneCountry(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    return yield api.countryOne({ id });
  }

  *fetch(fn: () => Generator | Promise<any>): any {
    try {
      this.setStatus("fetching");
      const value = fn();

      if (isGenerator(value)) {
        const generator = value;
        let result;
        let next = generator.next();
        while (!next.done) {
          try {
            result = yield next.value;
            next = generator.next(result);
          } catch (error) {
            next = generator.throw(error);
          }
        }
        this.setStatus("ok");
        return next.value;
      } else {
        const result = yield value;
        this.setStatus("ok");
        return result;
      }
    } catch (error) {
      this.showApiError(error);
      this.setStatus("error");
      throw error;
    }
  }

  getState(): InitializeParams {
    return {
      uiState: {
        currentPage: this.currentPage,
        searchValue: this.searchValue,
        whereFilters: this.whereFilters,
        orderBy: this.grid.sorting,
      },
    };
  }

  destroy() {
    if (
      this.debounceLoadFirstPage &&
      typeof this.debounceLoadFirstPage.cancel === "function"
    ) {
      this.debounceLoadFirstPage.cancel();
    }

    this.clearList();
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }
}
